import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import { accountingApi } from '../../../config/apiClient';
import { ACCOUNTING_API } from '../../../config/api';

interface ExpenseCategory {
  id: number;
  name: string;
  sortOrder: number;
  isActive: boolean;
}

interface CashExpenseCategoriesModalProps {
  open: boolean;
  onClose: () => void;
}

const errorMessage = (err: any, fallback: string) => err?.response?.data?.message || fallback;

const CashExpenseCategoriesModal: React.FC<CashExpenseCategoriesModalProps> = ({ open, onClose }) => {
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await accountingApi.get(ACCOUNTING_API.CASHBOX_EXPENSE_CATEGORIES(true));
      const list: any[] = res.data?.data || [];
      setCategories(list.map(c => ({
        id: Number(c.id),
        name: String(c.name ?? ''),
        sortOrder: Number(c.sortOrder ?? 0),
        isActive: Boolean(c.isActive),
      })));
    } catch (err) {
      setError(errorMessage(err, 'Gider türleri yüklenemedi.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setNewName('');
    setEditingId(null);
    load();
  }, [open, load]);

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusyId('new');
    setError(null);
    try {
      await accountingApi.post(ACCOUNTING_API.CASHBOX_EXPENSE_CATEGORY_CREATE, { Name: name });
      setNewName('');
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Gider türü eklenemedi.'));
    } finally {
      setBusyId(null);
    }
  };

  const update = async (id: number, body: { Name?: string; IsActive?: boolean }) => {
    setBusyId(id);
    setError(null);
    try {
      await accountingApi.post(ACCOUNTING_API.CASHBOX_EXPENSE_CATEGORY_UPDATE(id), body);
      await load();
      return true;
    } catch (err) {
      setError(errorMessage(err, 'Gider türü güncellenemedi.'));
      return false;
    } finally {
      setBusyId(null);
    }
  };

  const handleSaveEdit = async () => {
    if (editingId == null) return;
    const name = editingName.trim();
    if (!name) return;
    if (await update(editingId, { Name: name })) setEditingId(null);
  };

  const handleDelete = async (category: ExpenseCategory) => {
    if (!window.confirm(`"${category.name}" gider türü silinsin mi? Geçmiş giderlerdeki tür adı korunur.`)) return;
    setBusyId(category.id);
    setError(null);
    try {
      await accountingApi.post(ACCOUNTING_API.CASHBOX_EXPENSE_CATEGORY_DELETE(category.id), {});
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Gider türü silinemedi.'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Kasa Gider Türleri
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Şubeler kasadan gider eklerken bu listeden tür seçer. Pasif türler gider ekleme ekranında görünmez.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            size="small"
            fullWidth
            label="Yeni gider türü"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
            inputProps={{ maxLength: 100 }}
          />
          <Button
            variant="contained"
            startIcon={busyId === 'new' ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            onClick={handleAdd}
            disabled={!newName.trim() || busyId !== null}
          >
            Ekle
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>
        ) : categories.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            Tanımlı gider türü yok.
          </Typography>
        ) : (
          <List dense disablePadding>
            {categories.map(category => {
              const isEditing = editingId === category.id;
              const busy = busyId === category.id;
              return (
                <ListItem
                  key={category.id}
                  divider
                  sx={{ gap: 1, opacity: category.isActive ? 1 : 0.6 }}
                  secondaryAction={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Tooltip title={category.isActive ? 'Aktif' : 'Pasif'}>
                        <span>
                          <Switch
                            size="small"
                            checked={category.isActive}
                            disabled={busyId !== null}
                            onChange={(_, checked) => update(category.id, { IsActive: checked })}
                          />
                        </span>
                      </Tooltip>
                      {isEditing ? (
                        <IconButton size="small" color="primary" onClick={handleSaveEdit} disabled={busyId !== null}>
                          <CheckIcon fontSize="small" />
                        </IconButton>
                      ) : (
                        <IconButton
                          size="small"
                          onClick={() => { setEditingId(category.id); setEditingName(category.name); }}
                          disabled={busyId !== null}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton size="small" color="error" onClick={() => handleDelete(category)} disabled={busyId !== null}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  }
                >
                  <Box sx={{ pr: 16, flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    {busy && <CircularProgress size={14} />}
                    {isEditing ? (
                      <TextField
                        size="small"
                        fullWidth
                        autoFocus
                        value={editingName}
                        onChange={e => setEditingName(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleSaveEdit();
                          if (e.key === 'Escape') setEditingId(null);
                        }}
                        inputProps={{ maxLength: 100 }}
                      />
                    ) : (
                      <Typography variant="body2">{category.name}</Typography>
                    )}
                  </Box>
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Kapat</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CashExpenseCategoriesModal;
